import {
  FEED_IMPORT_BASE,
  META_IMPORT_SECRET_BASE,
  SOCIAL_POSTS_BASE,
  adsAssistantClientFetch,
  type AdsAssistantFetchResult,
} from "./adsAssistantClientHttp";
import type { AdsActivityFreshAchievement } from "./adsAssistantClientEngagement";
import type { AdsAssistantSocialPostRow } from "./adsAssistantClientTypes";

export async function listAdsSocialPosts(params?: {
  platform?: string;
  location_id?: string;
  /** Case-insensitive substring match on post text (SQL ILIKE). */
  search?: string;
  /** Only posts indexed by the signed-in user (`ingested_by_user_id`). */
  mine_only?: boolean;
  tag_id?: string;
  tag_slug?: string;
  green_only?: boolean;
  /** Slice 18f: only rows where `template_id IS NULL` (batch templateize candidates). */
  untemplated_only?: boolean;
  /** Default server behavior is `published_at_desc`. */
  sort?: "published_at_desc" | "sale_units_desc";
  limit?: number;
  offset?: number;
  includeCategories?: boolean;
  includeLatestAnalysis?: boolean;
  includeSaleTotals?: boolean;
  includeTags?: boolean;
  /** Slice 17c: attach `post_media` from `ads_assistant_social_post_media`. */
  includeMedia?: boolean;
  /** When true with `includeMedia`, server adds short-lived `signed_url` on each item. */
  sign_media?: boolean;
}): Promise<
  AdsAssistantFetchResult<{
    ok: true;
    posts: AdsAssistantSocialPostRow[];
    total: number;
    limit?: number;
    offset?: number;
  }>
> {
  const sp = new URLSearchParams();
  if (params?.platform) sp.set("platform", params.platform);
  if (params?.location_id) sp.set("location_id", params.location_id);
  if (params?.search?.trim()) sp.set("search", params.search.trim());
  if (params?.mine_only === true) sp.set("mine_only", "true");
  if (params?.tag_id?.trim()) sp.set("tag_id", params.tag_id.trim());
  if (params?.tag_slug?.trim()) sp.set("tag_slug", params.tag_slug.trim().toLowerCase());
  if (params?.green_only === true) sp.set("green_only", "true");
  if (params?.untemplated_only === true) sp.set("untemplated_only", "true");
  if (params?.sort && params.sort !== "published_at_desc") sp.set("sort", params.sort);
  if (typeof params?.limit === "number" && Number.isFinite(params.limit)) sp.set("limit", String(params.limit));
  if (typeof params?.offset === "number" && Number.isFinite(params.offset)) sp.set("offset", String(params.offset));
  const inc: string[] = [];
  if (params?.includeCategories) inc.push("categories");
  if (params?.includeLatestAnalysis) inc.push("latest_analysis");
  if (params?.includeSaleTotals) inc.push("sale_totals");
  if (params?.includeTags) inc.push("tags");
  if (params?.includeMedia) inc.push("media");
  if (inc.length) sp.set("include", inc.join(","));
  if (params?.sign_media === true) sp.set("sign_media", "true");
  const q = sp.toString();
  return adsAssistantClientFetch(`${SOCIAL_POSTS_BASE}${q ? `?${q}` : ""}`, { method: "GET" });
}

export async function getAdsMetaImportSecret(): Promise<
  AdsAssistantFetchResult<{
    ok: true;
    configured: boolean;
    updated_at: string | null;
    encryption_configured: boolean;
    can_edit_meta_token: boolean;
  }>
> {
  return adsAssistantClientFetch(META_IMPORT_SECRET_BASE, { method: "GET" });
}

export async function putAdsMetaImportSecret(body: {
  page_access_token?: string;
  clear?: boolean;
}): Promise<AdsAssistantFetchResult<{ ok: true; saved?: boolean; cleared?: boolean }>> {
  return adsAssistantClientFetch(META_IMPORT_SECRET_BASE, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteAdsMetaImportSecret(): Promise<AdsAssistantFetchResult<{ ok: true; cleared?: boolean }>> {
  return adsAssistantClientFetch(META_IMPORT_SECRET_BASE, { method: "DELETE" });
}

/** Sales / trainer / marketing / admin / owner / dev / platform; upsert triggers stub analysis (uses active policies). */
export async function importAdsFeed(body: {
  mode:
    | "ghl_social_planner"
    | "facebook_graph_fetch"
    | "facebook_graph_paste"
    | "google_local_posts_paste"
    | "google_gbp_fetch";
  platform?: "facebook" | "google";
  location_id?: string | null;
  payload?: unknown;
  page_id?: string;
  page_access_token?: string;
  limit?: number;
  days_back?: number;
  ghl_connection_id?: string | null;
  /** GHL only: re-tag indexed posts from stored account ids without fetching new posts. */
  reconcile_only?: boolean;
}): Promise<
  AdsAssistantFetchResult<{
    ok: true;
    imported: number;
    skipped: number;
    unchanged?: number;
    location_corrected?: number;
    filtered_other_locations?: number;
    unmapped_accounts?: number;
    scanned?: number;
    mapping_diagnostics?: {
      locations_count?: number;
      locations_with_facebook_id?: number;
      ghl_accounts_total?: number;
      ghl_accounts_mapped?: number;
      match_by_page_id?: number;
      match_by_name?: number;
    };
    total_received: number;
    errors: string[];
    ghl_location_id?: string;
    ghl_connection_id?: string;
  }>
> {
  return adsAssistantClientFetch(FEED_IMPORT_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function upsertAdsSocialPost(body: {
  platform: string;
  external_post_id: string;
  /**
   * Gamification Phase 1: optional. Sales role omits it; trainer+ may supply
   * an override when backfilling. When omitted, the server stamps
   * `logged_at = published_at = now()` so streaks/today counts stay honest.
   */
  published_at?: string;
  text_content?: string;
  location_id?: string | null;
  template_id?: string | null;
  category_ids?: string[];
  media_urls?: string[];
  media_type?: "text" | "image" | "video" | "carousel" | "unknown";
  thumbnail_url?: string | null;
  engagement_json?: Record<string, unknown>;
  raw_payload?: Record<string, unknown>;
  creative_description?: string;
  quality_rating?: "good" | "bad";
  quality_note?: string;
  /** Non-text CTA hint (e.g. Google Call now); stored in `raw_payload.platform_cta`. */
  platform_cta?: string | null;
  slot_fill_json?: Record<string, unknown>;
  promoted_exemplar?: boolean;
  /** Up to five org tag UUIDs (validated server-side). */
  tag_ids?: string[];
  source?: "api_ingest" | "manual_seed" | "backfill";
}): Promise<
  AdsAssistantFetchResult<{ ok: true; post: AdsAssistantSocialPostRow; fresh_achievements?: AdsActivityFreshAchievement[] }>
> {
  return adsAssistantClientFetch(SOCIAL_POSTS_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function patchAdsSocialPost(
  id: string,
  patch: Partial<{
    text_content: string;
    published_at: string;
    platform: string;
    media_type: "text" | "image" | "video" | "carousel" | "unknown";
    location_id: string | null;
    ingested_by_user_id?: string | null;
    template_id: string | null;
    category_ids: string[];
    creative_description?: string;
    quality_rating?: "good" | "bad";
    quality_note?: string;
    platform_cta?: string | null;
    slot_fill_json: Record<string, unknown> | null;
    promoted_exemplar: boolean;
    green_portable?: boolean;
    tag_ids?: string[];
  }>
): Promise<AdsAssistantFetchResult<{ ok: true; post: AdsAssistantSocialPostRow }>> {
  const url = `${SOCIAL_POSTS_BASE}?id=${encodeURIComponent(id)}`;
  return adsAssistantClientFetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export async function deleteAdsSocialPost(
  id: string
): Promise<AdsAssistantFetchResult<{ ok: true }>> {
  const url = `${SOCIAL_POSTS_BASE}?id=${encodeURIComponent(id)}`;
  return adsAssistantClientFetch(url, { method: "DELETE" });
}

export async function fetchAdsAttributionOptions(): Promise<
  AdsAssistantFetchResult<{
    ok: true;
    locations: { id: string; name: string }[];
    users: { id: string; display_name: string }[];
  }>
> {
  return adsAssistantClientFetch("/api/ads-assistant/attribution-options");
}
